import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  CreateJobParameters,
  GenerateDataClassicOptions,
  GenerateDataPmOptions,
  OptimizeClassicOptions,
  OptimizePmOptions,
} from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { GeodataSetupContextType } from '../../../state/geodata-setup-state';
import { useGeodataCreateJobMutation } from '../gcp-page/use-geodata-create-job-mutation';
import { UploadStepStatus } from './use-upload-boundary-polygons-step';

export const useStartCloudProcessStep = (
  context: GeodataSetupContextType | undefined,
  geodataId: string | undefined,
  projectId: string | undefined,
  controlImagesStepStatus: UploadStepStatus,
) => {
  const jobCreationInitiatedRef = useRef(false);

  const { handleError, buildErrorList } = useErrorHandling();
  const { createJobAsync, isPending, jobCreated, resetJobCreated } = useGeodataCreateJobMutation();

  const skipGcpStep = useMemo(() => {
    return context?.skipGcpStep ?? false;
  }, [context]);

  const requestBody = useMemo<CreateJobParameters | undefined>(() => {
    if (!geodataId) {
      return undefined;
    }

    const isPm = context?.selectedPackage === 'PM';

    const jobBody:
      | GenerateDataClassicOptions
      | GenerateDataPmOptions
      | OptimizeClassicOptions
      | OptimizePmOptions = skipGcpStep
      ? { type: isPm ? 'generate_data_pm' : 'generate_data_classic' }
      : isPm
        ? { type: 'optimize_pm' }
        : { type: 'optimize_classic', sendEmailNotification: true };

    return {
      path: { geodataId },
      body: jobBody,
    };
  }, [geodataId, skipGcpStep, context?.selectedPackage]);

  const createJobWithErrorHandling = useCallback(async () => {
    if (!requestBody) {
      return;
    }

    try {
      await createJobAsync(requestBody);
    } catch (error) {
      handleError(error);
    }
  }, [createJobAsync, handleError, requestBody]);

  const errorList = useMemo(
    () =>
      buildErrorList({
        retryCallback: createJobWithErrorHandling,
      }),
    [buildErrorList, createJobWithErrorHandling],
  );

  // Map job creation state to StepStatus
  const uploadStepStatus = useMemo<UploadStepStatus>(() => {
    if (!context || context.useInteractiveSetup) {
      // Skip this step if using interactive setup
      return 'success';
    }
    if (jobCreated) {
      return 'success';
    }
    if (isPending) {
      return 'loading';
    }
    // Check if we should be pending (control images step succeeded but job not started)
    if (controlImagesStepStatus === 'success' && !jobCreationInitiatedRef.current) {
      return 'pending';
    }
    // If we've initiated but failed, check error state
    if (jobCreationInitiatedRef.current && !jobCreated && !isPending) {
      return 'error';
    }
    return 'pending';
  }, [context, jobCreated, isPending, controlImagesStepStatus]);

  // Auto-start job creation when conditions are met
  useEffect(() => {
    if (!geodataId || !projectId || !context) {
      return;
    }

    // Skip if using interactive setup
    if (context.useInteractiveSetup) {
      return;
    }

    // Prevent duplicate job creation initiation in React Strict Mode
    if (jobCreationInitiatedRef.current || jobCreated || isPending) {
      return;
    }

    // Step 5: Start processing job (only if control images step succeeded and there are no errors)
    if (controlImagesStepStatus === 'success') {
      jobCreationInitiatedRef.current = true;
      void createJobWithErrorHandling();
    }
  }, [
    geodataId,
    projectId,
    context,
    controlImagesStepStatus,
    jobCreated,
    isPending,
    createJobWithErrorHandling,
  ]);

  // Reset job creation initiation when geodataId changes
  useEffect(() => {
    jobCreationInitiatedRef.current = false;
    resetJobCreated();
  }, [geodataId, resetJobCreated]);

  return {
    uploadStepStatus,
    errorList,
  };
};
